import Dexie from 'dexie';
import { IDexieConfiguration } from '@/models/dexie/DexieConfiguration';
import { TableMap } from '@/models/dexie/ITable';
import { IDexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { IAppSettings, AppSettings } from '@/models/settings/AppSettings';
import { IUserProfile } from '@/models/dexie/UserProfile';
import { LoggerService } from '@/utils';
import { IDataSetBase } from '@/models/dataset/DataSetBase';
import { IAxCustomer } from '@/models/axdata/AxCustomer';
import { IAxMachineModel } from '@/models/axdata/AxMachineModel';
import { IDexieQuickEditModel } from '@/models/dexie/DexieQuickEditModel';
import { IDexieFilter } from '@/models/dexie/DexieFilter';
import { DexieMyMillUnitStatus } from '@/models/dexie/myMill/DexieMyMillUnitStatus';
import { DexieMyMillUnitItem } from '@/models/dexie/myMill/DexieMyMillUnitItem';

class DexieDB extends Dexie {

    // Declare implicit table properties.
    // (just to inform Typescript. Instanciated by Dexie in stores() method)
    private configuration: Dexie.Table<IDexieConfiguration, number>; // number = type of the primkey
    private machinesConfiguration: Dexie.Table<IDexieConfiguration, number>; // number = type of the primkey
    private mymillImportConfiguration: Dexie.Table<IDexieConfiguration, number>; // number = type of the primkey
    private samplesConfiguration: Dexie.Table<IDexieConfiguration, number>; // number = type of the primkey
    private unitsConfiguration: Dexie.Table<IDexieConfiguration, number>; // number = type of the primkey
    private dataSet: Dexie.Table<IDataSetBase, number>;
    private unsyncedEvents: Dexie.Table<IDexieDataSetEvent, number>;
    private syncedEvents: Dexie.Table<IDexieDataSetEvent, number>;
    private appSettings: Dexie.Table<IAppSettings, number>;
    private userProfiles: Dexie.Table<IUserProfile, number>;
    private axCustomers: Dexie.Table<IAxCustomer, number>;
    private axMachineModels: Dexie.Table<IAxMachineModel, number>;
    private quickViewConfigurations: Dexie.Table<{ id?: number, data: IDexieQuickEditModel}, number>;

    private configurationUpdates: Dexie.Table<IAxMachineModel, number>;

    private userWhitelist: Dexie.Table<{ id?: number, list: string }, number>;

    private myMillUnitStatus: Dexie.Table<DexieMyMillUnitStatus, number>;
    private myMillUnitItems: Dexie.Table<DexieMyMillUnitItem, number>;

    private unsyncedFiltersNew: Dexie.Table<IDexieFilter, number>;
    private syncedFiltersNew: Dexie.Table<IDexieFilter, number>;

    private tableMap: TableMap = new TableMap();

    constructor(dbName: string) {
        super(dbName);

        /**
         * WARNING!
         * If you want to add/remove/change tables, you have to add
         * a new version, otherwise you may cause exceptions on
         * dev/prod servers!
         */

        this.version(1).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            unsyncedFilters: '++id',
            syncedFilters: '++id',
        });
        this.version(2).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            unsyncedFilters: '++id',
            syncedFilters: '++id',
            userWhitelist: '++id',
        });
        this.version(3).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            unsyncedFilters: '++id',
            syncedFilters: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',
        });
        this.version(4).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            unsyncedFilters: '++id',
            syncedFilters: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',
        });
        this.version(5).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            unsyncedFilters: '++id',
            syncedFilters: '++id',
            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id',
        });
        this.version(6).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id',
        });
        /**
         * In v7 model for appsettings changed
         */
        this.version(7).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id',
        });
        /**
         * In v8 index fitlers by uuid
         */
        this.version(8).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id,filterUuid',
        });
        /**
         * In v9 models for MyMill units
         */
        this.version(9).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            myMillUnitItems: '++id,uuid,name',
            myMillUnitStatus: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id,filterUuid',
        });
        /**
         * In v10 models for MyMill import configuration
         */
        this.version(10).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            mymillImportConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            myMillUnitItems: '++id,uuid,name',
            myMillUnitStatus: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id,filterUuid',
        });
        /**
         * In v11 models for MyMill inmport information in dataset added
         * see: TTDB-930
         */
        this.version(11).stores({
            configuration: '++id',
            machinesConfiguration: '++id',
            mymillImportConfiguration: '++id',
            samplesConfiguration: '++id',
            unitsConfiguration: '++id',
            dataSet: '++id,dataSetId',
            unsyncedEvents: '++id,date',
            syncedEvents: '++id,date',
            appSettings: '++id',
            userProfiles: '++id',
            axCustomers: '++id,customerName,customerNumber',
            axMachineModels: '++id,machineId,model',
            quickViewConfigurations: '++id',
            configurationUpdates: '++id,name',
            userWhitelist: '++id',

            myMillUnitItems: '++id,uuid,name',
            myMillUnitStatus: '++id',

            unsyncedFiltersNew: '++id',
            syncedFiltersNew: '++id,filterUuid',
        });
        // The following line is needed if your typescript
        // is compiled using babel instead of tsc:
        this.configuration = this.table(this.tableMap.configuration);
        this.machinesConfiguration = this.table(this.tableMap.machinesConfiguration);
        this.mymillImportConfiguration = this.table(this.tableMap.mymillImportConfiguration);
        this.samplesConfiguration = this.table(this.tableMap.samplesConfiguration);
        this.unitsConfiguration = this.table(this.tableMap.unitsConfiguration);
        this.dataSet = this.table(this.tableMap.dataSet);
        this.unsyncedEvents = this.table(this.tableMap.unsyncedEvents);
        this.syncedEvents = this.table(this.tableMap.syncedEvents);
        this.appSettings = this.table(this.tableMap.appSettings);
        this.userProfiles = this.table(this.tableMap.userProfiles);
        this.axCustomers = this.table(this.tableMap.axCustomers);
        this.axMachineModels = this.table(this.tableMap.axMachineModels);
        this.configurationUpdates = this.table(this.tableMap.configurationUpdates);
        this.userWhitelist = this.table(this.tableMap.userWhitelist);
        this.quickViewConfigurations = this.table(this.tableMap.quickEditConfigurations);

        this.myMillUnitItems = this.table(this.tableMap.myMillUnitItems);
        this.myMillUnitStatus = this.table(this.tableMap.myMillUnitStatus);

        this.unsyncedFiltersNew = this.table(this.tableMap.unsyncedFiltersNew);
        this.syncedFiltersNew = this.table(this.tableMap.syncedFiltersNew);
    }

    public getTables(): TableMap {
        return this.tableMap;
    }

    /**
     * Add a new data object into a table
     * @param tableName     Name of the table
     * @param data          The json ojbject with the data to store
     */
    public async add<T>(tableName: string, data: T): Promise<number> {
        const tableRef = this.getTableRef<T>(tableName);

        return new Promise((resolve, reject) => {
            if (tableRef !== undefined) {
                resolve(tableRef.add(data));
            } else {
                reject(`Cant find table ${tableName} in DexieDB to put data`);
            }
        });
    }

    /**
     * Put/Update a data object into a table
     * @param tableName     Name of the table
     * @param data          The json ojbject with the data to store.
     *                      The id of the object must match to an existing on,
     *                      otherwise put will create a new entry!
     */
    public async put<T>(tableName: string, data: T): Promise<number> {
        const tableRef = this.getTableRef<T>(tableName);

        return new Promise((resolve, reject) => {
            if (tableRef !== undefined) {
                resolve(tableRef.put(data));
            } else {
                reject(`Cant find table ${tableName} in DexieDB to put data`);
            }
        });
    }

    /**
     * BulkPut an array of data objects into a table
     * @param tableName     Name of the table
     * @param data          The json array with the data objects to store.
     */
    public async bulkPut<T>(tableName: string, data: T[]): Promise<number> {
        const tableRef = this.getTableRef<T>(tableName);

        return new Promise((resolve, reject) => {
            if (Array.isArray(data) === false) {
                reject(`data must be an array`);
            }
            if (tableRef !== undefined) {
                resolve(tableRef.bulkPut(data));
            } else {
                reject(`Cant find table ${tableName} in DexieDB to put data`);
            }
        });
    }

    /**
     * Delete an entry
     * @param tableName     Name of the Table
     * @param key           The id of the entry
     */
    public async del<T>(tableName: string, key?: number): Promise<void> {
        const tableRef = this.getTableRef<T>(tableName);

        return new Promise((resolve, reject) => {
            if (tableRef !== undefined && key != null) {
                resolve(tableRef.delete(key));
            } else {
                reject(`Cant find table ${tableName} in DexieDB to delete data with id: ${key}`);
            }
        });
    }

    /**
     * Clear all records from a table
     * @param tableName     Name of the table
     */
    public async clear<T>(tableName: string): Promise<void> {
        const tableRef = this.getTableRef<T>(tableName);

        return new Promise((resolve, reject) => {
            if (tableRef !== undefined) {
                resolve(tableRef.clear());
            } else {
                reject(`Cant find table ${tableName} in DexieDB to clear data`);
            }
        });
    }

    /**
     * Add or Update a data object into a table
     * @param tableName     Name of the table
     * @param key           Key of the entrie
     * @param data          The json ojbject with the data to store
     */
    public update(tableName: string, key: number, data: object): Promise<number> {
        const descriptor = Object.getOwnPropertyDescriptor(this, tableName);

        return new Promise((resolve, reject) => {
            if (descriptor !== undefined && descriptor.value.put) {
                resolve(descriptor.value.update(data));
            } else {
                reject(`Cant find table ${tableName} in DexieDB to update data`);
            }
        });
    }

    /**
     * Get the dexie table object from the dexie object.
     *
     * @typeparam T Type of the table
     * @param tableName name of the table
     */
    public getTable<T>(tableName: string) {
        const descriptor = Object.getOwnPropertyDescriptor(this, tableName);
        let result: Dexie.Table<T, number> = {} as any;
        if (descriptor !== undefined) {
            if (descriptor.value) {
                result = descriptor.value as Dexie.Table<T, number>;
            } else if (descriptor.get) {
                result = descriptor.get() as Dexie.Table<T, number>;
            } else {
                LoggerService.LogError(`Something went wrong on getting table ${tableName}`);
            }
        } else {
            LoggerService.LogError(`Couldn't find table ${tableName}`);
        }

        return result;
    }

    private getTableRef<T>(tableName: string): Dexie.Table<T, number> {
        const descriptor = Object.getOwnPropertyDescriptor(this, tableName);
        let result!: Dexie.Table<T, number>;
        if (descriptor !== undefined) {
            if (descriptor.value) {
                result = descriptor.value;
            } else if (descriptor.get) {
                result = descriptor.get();
            } else {
                LoggerService.LogError(`Something went wrong on getting tableRef ${tableName}`);
            }
        } else {
            LoggerService.LogError(`Couldn't find tableRef ${tableName}`);
        }

        return result as Dexie.Table<T, number>;
    }

}

export default DexieDB;
